import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  X, 
  Home, 
  Package, 
  ShoppingCart, 
  ClipboardList, 
  User, 
  MapPin, 
  Phone,
  Flame,
  LogOut,
  LogIn
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MobileMenu = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname]);

  // Bloquear scroll cuando el menú está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const mainLinks = [
    { to: '/', icon: Home, label: 'Inicio' },
    { to: '/productos', icon: Package, label: 'Productos' },
    { to: '/carrito', icon: ShoppingCart, label: 'Carrito' },
    { to: '/cobertura', icon: MapPin, label: 'Zonas de Cobertura' },
    { to: '/contacto', icon: Phone, label: 'Contacto' },
  ];

  const authLinks = isAuthenticated
    ? [
        { to: '/pedidos', icon: ClipboardList, label: 'Mis Pedidos' },
        { to: '/cuenta', icon: User, label: 'Mi Cuenta' },
      ]
    : [
        { to: '/login', icon: LogIn, label: 'Iniciar Sesión' },
        { to: '/register', icon: User, label: 'Crear Cuenta' },
      ];

  const handleLogout = () => {
    logout();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="absolute left-0 top-0 bottom-0 w-[280px] max-w-[80vw] bg-white shadow-2xl animate-slideInRight">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-background">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-2" onClick={onClose}>
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Flame size={24} className="text-white" />
              </div>
              <span className="font-display font-bold text-xl text-text">
                SammyGas
              </span>
            </Link>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-xl hover:bg-gray-50 transition-colors touch-manipulation"
              aria-label="Cerrar menú"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          {isAuthenticated && user && (
            <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-text truncate">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user.email}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="py-2 overflow-y-auto h-[calc(100%-140px)]">
          {/* Main Links */}
          <div className="px-2 mb-2">
            <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Menú Principal
            </p>
            {mainLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`
                  flex items-center gap-3 px-3 py-3 mx-1 rounded-xl
                  transition-colors touch-manipulation
                  ${location.pathname === link.to
                    ? 'bg-primary/10 text-primary'
                    : 'text-text hover:bg-gray-50'
                  }
                `}
                onClick={onClose}
              >
                <link.icon size={20} />
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Auth Links */}
          <div className="px-2 mb-2">
            <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {isAuthenticated ? 'Mi Cuenta' : 'Acceso'}
            </p>
            {authLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`
                  flex items-center gap-3 px-3 py-3 mx-1 rounded-xl
                  transition-colors touch-manipulation
                  ${location.pathname === link.to
                    ? 'bg-primary/10 text-primary'
                    : 'text-text hover:bg-gray-50'
                  }
                `}
                onClick={onClose}
              >
                <link.icon size={20} />
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}
            
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-3 mx-1 rounded-xl text-error hover:bg-red-50 transition-colors touch-manipulation"
              >
                <LogOut size={20} />
                <span className="font-medium">Cerrar Sesión</span>
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-background">
          <p className="text-xs text-center text-gray-400">
            © 2026 SammyGas. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
